import React, {useState, useEffect, useRef} from 'react';
import { DndProvider} from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import axios from 'axios';
import { saveAs } from 'file-saver';

import Arrow from '../../components/Arrow.js';
import Loading from '../../components/Loading.js';
import InfoPopup from '../../components/InfoPopup.js';
import Confirmation from '../../components/Confirmation.js';

import '../../styles/live/MeaningfulWork.css';

const API_ROUTE = process.env.REACT_APP_API_URL;

function MeaningfulWork({cbPrevious, cbFinish, user, download}) {
  const [name, setName] = useState("");
  const [eventName, setEventName] = useState("");
  const [eventDate, setEventDate] = useState(new Date());
  const [infoPopup, setInfoPopup] = useState(null);
  const [loading, setLoading] = useState(false);

  const [longArrowLen, setLongArrowLen] = useState(100);
  const longArrowContainerRef = useRef(null);
  const [data, setData] = useState({'driver-value': "", 'driver-desc': "", 'driver-altlabel': "", 'driver-altdesc': "", 'result-value': "", 'result-desc': "", 'result-alt': "", 'accelerator-0-value': "", 'accelerator-0-desc': "", 'accelerator-1-value': "", 'accelerator-1-desc': "", 'accelerator-2-value': "", 'accelerator-2-desc': "", 'accelerator-3-value': "", 'accelerator-3-desc': "", 'mb-body': ""})  
  const [resettingData, setResettingData] = useState(false);

  const isNonResizable = download ? "" : " nonresizable";
  const textAreaExpand = download ? " fill-content" : "";
  
  const year = (new Date()).getFullYear();

  const handleTextChange = (type, text) => {
    let newData = {...data};
    newData[type] = text;
    setData(newData);
  }

  const saveData = (next) => {
    let dataUpdate = {data: {}};
    for (let key in data) {
      dataUpdate.data[key] = data[key];
    }

    if (user) {
      dataUpdate['uid'] = user;
    }

    const url = `${API_ROUTE}/response/meaningfulwork`;

    axios.put(url, dataUpdate, {
      withCredentials: true,
      headers: {
        'Content-Type': 'application/json'
      }
    }).then((res) => {
      const newInfoPopup = <InfoPopup message={res.data.message}
                                      success={res.data.success} />
      setInfoPopup(newInfoPopup);
      setTimeout(() => {
        setInfoPopup(null);
        if (next) {
          next();
        }
      }, process.env.REACT_APP_INFO_TIMEOUT);
    }).catch((err) => {
      console.log(err);
      const newInfoPopup = <InfoPopup message={err.response.data.message}
                                      success={err.response.data.success} />
      setInfoPopup(newInfoPopup);
      setTimeout(() => {
        setInfoPopup(null);
      }, process.env.REACT_APP_INFO_TIMEOUT);
    });
  };

  const downloadFile = (filename) => {
    setLoading(true);
    axios.get(`${API_ROUTE}/event/event_output`, {
      withCredentials: true,
      params: new URLSearchParams({uid: user, page: 'mw'}),
      responseType: 'arraybuffer'
    }).then((res) => {
      const pdf = new Blob([res.data], { type: 'application/pdf' });
      saveAs(pdf, filename);
      setLoading(false);
    }).catch((err) => {
      console.log(err);
      setLoading(false);
    });
  };
  
  const downloadOutput = (filename) => {
    saveData(() => downloadFile(filename));
  };

  const resetResponses = () => {
    const url = user ? `${API_ROUTE}/response/participant_byid` : `${API_ROUTE}/response/participant`;
    const options = user ? {
      withCredentials: true,
      params: new URLSearchParams({prework: true, uid: user})
    } : {
      withCredentials: true,
      params: new URLSearchParams({prework: true})
    };

    axios.get(url, options).then((res) => {
      let values = res.data.values;
      let definitions = res.data.descriptions;
      let originalIndexes = res.data.originalIndexes;
      let percentLived = res.data.percentLived;

      let newData = {...data};
      newData['driver-value'] = values[0];
      newData['driver-desc'] = definitions[0];
      newData['driver-altlabel'] = "";
      newData['driver-altdesc'] = "";
      newData['result-value'] = values[9];
      newData['result-desc'] = definitions[9];
      newData['result-alt'] = "";
      newData['accelerator-0-value'] = values[5];
      newData['accelerator-0-desc'] = "";
      newData['accelerator-1-value'] = values[6];
      newData['accelerator-1-desc'] = "";
      newData['accelerator-2-value'] = values[7];
      newData['accelerator-2-desc'] = "";
      newData['accelerator-3-value'] = values[8];
      newData['accelerator-3-desc'] = "";
      // newData['accelerator-values'] = `${values[5].toUpperCase()}\n\n↓\n${values[6].toUpperCase()}\n\n↓\n${values[7].toUpperCase()}\n\n↓\n${values[8].toUpperCase()}`;
      newData['mb-body'] = "";
      setData(newData);
    });
  };

  useEffect(() => {
    if (longArrowContainerRef.current) {
      setLongArrowLen(longArrowContainerRef.current.offsetHeight);
    }
  }, [longArrowContainerRef.current]);

  useEffect(() => {
    if (download) {
      const textareas = document.querySelectorAll('textarea.editable');
      textareas.forEach(textarea => {
        textarea.style.height = 'auto';
        textarea.style.height = textarea.scrollHeight + 'px';
        textarea.style.resize = 'none';
      })
    }
  }, [download]);

  useEffect(() => {
    const options = user ? {
      withCredentials: true,
      params: new URLSearchParams({prework: true, uid: user})
    } : {
      withCredentials: true,
      params: new URLSearchParams({prework: true})
    };
    axios.get(`${API_ROUTE}/response/meaningfulwork`, options).then((res) => {
      let newData = {...data};
      for (let key in data) {
        if (key in res.data.data) {
          newData[key] = res.data.data[key];
        }
      }
      setData(newData);
    }).catch((err) => {
      console.log(err);
      resetResponses();
    });

    const eventUrl = user ? `${API_ROUTE}/event/participant_event_byid` : `${API_ROUTE}/event/participant_event`;
    const eventOptions = user ? {
      withCredentials: true,
      params: new URLSearchParams({uid: user})
    } : {
      withCredentials: true
    };

    axios.get(eventUrl, eventOptions).then((res) => {
      let newEventName = res.data.event.eventName;
      let newEventDate = new Date(res.data.event.eventDate);

      setEventName(newEventName);
      setEventDate(newEventDate);
    });

    const userUrl = user ? `${API_ROUTE}/user/info_byid` : `${API_ROUTE}/user/info`;
    const userOptions = user ? {
      withCredentials: true,
      params: new URLSearchParams({uid: user})
    } : {
      withCredentials: true
    };

    axios.get(userUrl, userOptions).then((res) => {
      const newName = `${res.data.user.forename} ${res.data.user.surname}`;
      setName(newName);
    }).catch((err) => {
      console.log(err);
    });

    setTimeout(() => {
      if (download) {
        const textareas = document.querySelectorAll('.fill-content');
        textareas.forEach(textarea => {
          textarea.style.height = 'auto';
          textarea.style.height = `${textarea.scrollHeight}px`;
        });

        const divs = document.querySelectorAll('.contain');
        divs.forEach(div => {
          div.style['box-shadow'] = 'none';
        });
      }
    }, 500);
  }, []);

  return (
    <>
      <div>
        {loading && (
          <Loading />
        )}
      </div>
      <div>
        {resettingData && (
          <Confirmation cbDelete={() => {resetResponses(); setResettingData(false);}}
                        cbCancel={() => {setResettingData(false)}}
                        message={"Are you sure you want to reset the data on this page back to the participants values? Any information entered will be lost."}
                        buttonName={"Confirm Reset"} />
        )}
      </div>
      {infoPopup && (infoPopup)}
      <div className="container-meaningfulwork" style={download && {margin: "16px 12px", padding: 0}}>
        <div className="container-meaningfulworkheader">
          <div>
            <img id="logo" className="logo" src="/images/PursuitLogo.png" alt="Pursuit Inc." />
          </div>
          <div className="container-header-title">
            <div className="title-name">
              My Meaningful Work
            </div>
          </div>
          <div className="container-header-title">
            <div className="date">
              [{eventName}/{eventDate.toDateString()}]
            </div>
          </div>
        </div>

        <div className="container-meaningfulworkcontent">
          <div className="container-meaningfulworkcontentleft">
            <div>
              <div className="header">Driver Value</div>
              <div className="subheader">"The Space I Hold..."</div>
            </div>
            <div>
              <div className="contain">
                <textarea className={"editable value" + isNonResizable + textAreaExpand}
                          type="text"
                          value={data['driver-value']}
                          onChange={(e) => handleTextChange("driver-value", e.target.value)}
                          placeholder=""
                          rows={1} />
                          
                <textarea className={"editable description" + textAreaExpand}
                          type="text"
                          value={data['driver-desc']}
                          onChange={(e) => handleTextChange("driver-desc", e.target.value)}
                          placeholder=""
                          rows={4} />
              </div>
              <div className="container-arrow">
                <Arrow length={download ? 25 : 50} angle={'180'} colour={'#d9d9d9'} />
                <div className="arrow-text">Through</div>
              </div>
            </div>

            <div>
              <div className="subheader">"The Way I Relate..."</div>
            </div>
            <div>
              <div className="contain">
                <textarea className={"editable label" + isNonResizable + textAreaExpand}
                          type="text"
                          value={data['driver-altlabel']}
                          onChange={(e) => handleTextChange("driver-altlabel", e.target.value)}
                          placeholder=""
                          rows={1} />
                <textarea className={"editable description" + textAreaExpand}
                          type="text"
                          value={data['driver-altdesc']}
                          onChange={(e) => handleTextChange("driver-altdesc", e.target.value)}
                          placeholder=""
                          rows={1} />
              </div>
              <div className="container-arrow">
                <Arrow length={download ? 25 : 50} angle={'180'} colour={'#d9d9d9'} />
                <div className="arrow-text">Leads To</div>
              </div>
            </div>

            <div>
              <div className="header">Result Value</div>
              <div className="subheader">"The Possibilites That Inspire Me..."</div>
            </div>
            <div>
              <div className="contain">
                <textarea className={"editable value" + isNonResizable + textAreaExpand}
                          type="text"
                          value={data['result-value']}
                          onChange={(e) => handleTextChange("result-value", e.target.value)}
                          placeholder=""
                          rows={1} />
                          
                <textarea className={"editable description" + textAreaExpand}
                          type="text"
                          value={data['result-desc']}
                          onChange={(e) => handleTextChange("result-desc", e.target.value)}
                          placeholder=""
                          rows={4} />
              </div>
              
              <div className="container-arrow">
                <Arrow length={download ? 25 : 50} angle={'180'} colour={'#d9d9d9'} />
                <div className="arrow-text">Leads To</div>
              </div>
            </div>

            <div>
              <div className="subheader">"That Results In..."</div>
            </div>
            <div>
              <div className="contain">
                <textarea className={"editable description" + textAreaExpand}
                          type="text"
                          value={data['result-alt']}
                          onChange={(e) => handleTextChange("result-alt", e.target.value)}
                          placeholder=""
                          rows={4} />
              </div>
            </div>
          </div>

          <div className="container-meaningfulworkcontentarrows" ref={longArrowContainerRef}>
              <div className="container-arrow center-arrow">
                {/* <Arrow length={longArrowLen*77.5/100} angle={'-15'} colour={'#d9d9d9'} widthScale={0.075} /> */}
                <Arrow length={longArrowLen*75.5/100} angle={'-10'} colour={'#d9d9d9'} widthScale={0.05} />
              </div>
              <div className="container-arrow left-arrow">
                {/* <Arrow length={longArrowLen*3/4} angle={'0'} colour={'#e9e9e9'} widthScale={0.08} /> */}
                <Arrow length={longArrowLen*3/4} angle={'0'} colour={'#e9e9e9'} widthScale={0.0533} />
              </div>
              <div className="container-arrow top-arrow">
                {/* <Arrow length={longArrowLen*7/30} angle={'90'} colour={'#f2f2f2'} widthScale={0.225} /> */}
                <Arrow length={longArrowLen*3/20} angle={'90'} colour={'#f2f2f2'} widthScale={0.255} />
              </div>
          </div>
          
          <div className="container-meaningfulworkcontentright contain">
            {/* <textarea className="editable description"
                            type="text"
                            // value={`${values[5]}\n\n↓\n${values[6]}\n\n↓\n${values[7]}\n\n↓\n${values[8]}`}
                            // value={`${values[5].toUpperCase()}\n\n↓\n${values[6].toUpperCase()}\n\n↓\n${values[7].toUpperCase()}\n\n↓\n${values[8].toUpperCase()}`}
                            value={data['accelerator-values']}
                            onChange={(e) => handleTextChange("accelerator-values", e.target.value)}
                            placeholder=""
                            rows={20} /> */}
            
            <textarea className={"editable acc-value" + isNonResizable + textAreaExpand}
                          type="text"
                          value={data['accelerator-0-value']}
                          onChange={(e) => handleTextChange("accelerator-0-value", e.target.value)}
                          placeholder=""
                          rows={1} />
                          
            <textarea className={"editable description" + textAreaExpand}
                      type="text"
                      value={data['accelerator-0-desc']}
                      onChange={(e) => handleTextChange("accelerator-0-desc", e.target.value)}
                      placeholder=""
                      rows={4} />

            <div className="container-arrow">
              <Arrow length={25} angle={'180'} colour={'#000000'} />
            </div>
            
            <textarea className={"editable acc-value" + isNonResizable + textAreaExpand}
                          type="text"
                          value={data['accelerator-1-value']}
                          onChange={(e) => handleTextChange("accelerator-1-value", e.target.value)}
                          placeholder=""
                          rows={1} />
                          
            <textarea className={"editable description" + textAreaExpand}
                      type="text"
                      value={data['accelerator-1-desc']}
                      onChange={(e) => handleTextChange("accelerator-1-desc", e.target.value)}
                      placeholder=""
                      rows={4} />

            <div className="container-arrow">
              <Arrow length={25} angle={'180'} colour={'#000000'} />
            </div>
            
            <textarea className={"editable acc-value" + isNonResizable + textAreaExpand}
                          type="text"
                          value={data['accelerator-2-value']}
                          onChange={(e) => handleTextChange("accelerator-2-value", e.target.value)}
                          placeholder=""
                          rows={1} />
                          
            <textarea className={"editable description" + textAreaExpand}
                      type="text"
                      value={data['accelerator-2-desc']}
                      onChange={(e) => handleTextChange("accelerator-2-desc", e.target.value)}
                      placeholder=""
                      rows={4} />

            <div className="container-arrow">
              <Arrow length={25} angle={'180'} colour={'#000000'} />
            </div>
            
            <textarea className={"editable acc-value" + isNonResizable + textAreaExpand} 
                          type="text"
                          value={data['accelerator-3-value']}
                          onChange={(e) => handleTextChange("accelerator-3-value", e.target.value)}
                          placeholder=""
                          rows={1} />
                          
            <textarea className={"editable description" + textAreaExpand}
                      type="text"
                      value={data['accelerator-3-desc']}
                      onChange={(e) => handleTextChange("accelerator-3-desc", e.target.value)}
                      placeholder=""
                      rows={4} />
          </div>
          
          <div className="container-meaningfulworkcontentbottom">
            <div className="header mb-colour">"Meaningful Because..."</div>
            <textarea className="editable description mb-textarea"
                            type="text"
                            value={data['mb-body']}
                            onChange={(e) => handleTextChange("mb-body", e.target.value)}
                            placeholder=""
                            rows={3} />
          </div>
        </div>

        {!download && (
          <div className="container-buttons">
            <button className="button-live" onClick={() => saveData(cbPrevious)}>Save & Previous</button>
            <button className="button-live" onClick={() => saveData(() => {})}>Save</button>
            <button className="button-live" onClick={() => downloadOutput(`MyMeanginfulWork-${name}-${eventName}-${eventDate.toDateString()}.pdf`)}>Download</button>
            <button className="button-live" onClick={() => {setResettingData(true)}}>Reset</button>
            <button className="button-live" onClick={() => saveData(cbFinish)}>Finish</button>
          </div>
        )}

        {download && (
          <div className="container-printfooter">
            <p>Copyright © 2007-{year} Pursuit Development Labs Inc. All Rights Reserved.</p>
          </div>
        )}
      </div>
    </>
  );
}

export default MeaningfulWork;
